import { Divider, Tooltip, Typography } from 'antd';
import { NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import type { TSidebarItem } from '../../../../types/ux/sidebarItem.ux.type';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux.hook';
import {
  layoutSelector,
  onChangePageTitle,
} from '../../../../app/features/layout/layout.slice';
import { EMPTY_TEXT } from '../../../../utils/enum/defaultParams.enum';
import {
  AppRoutes,
  RedirectRoutes,
} from '../../../../utils/mapping/appRoutes.mapping';
import { useEffect, useMemo } from 'react';
import { SIDEBAR_REDIRECT_MAP } from './Utils/sidebarActiveRoutes.config';
import { PageTitle } from '../../../../types/ux/pageTitle.ux.type';

type Props = {
  option: TSidebarItem;
};

const SidebarItem = ({ option }: Props) => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const fromSumary = query.has('fromSumary');
  const dispatch = useAppDispatch();
  const { openSidebar: open } = useAppSelector(layoutSelector);

  const onChangeOption = async () => {
    const pageTitle: PageTitle = { title: option.label, icon: option.icon };
    if (!option.targetBlank) {
      dispatch(onChangePageTitle(pageTitle));
    }
  };

  const isActiveCondition = useMemo(() => {
    const currentPath = location.pathname;
    if (currentPath === option.link) return true;
    // Si se abre un nueva solicitud o se crea una ruta verifica;
    // si viene from sumary para saber que boton activar
    const isCreationPath = [
      RedirectRoutes.creationRoute,
      RedirectRoutes.newRequest,
    ].includes(currentPath);
    if (isCreationPath) {
      return fromSumary
        ? option.link === AppRoutes.requestsSumary
        : option.link === AppRoutes.requests;
    }

    // Si el valor mapeado para la ruta actual coincide con el link de este botón, se activa.
    return SIDEBAR_REDIRECT_MAP[currentPath] === option.link;
  }, [location.pathname, option.link, fromSumary]);

  useEffect(() => {
    if (isActiveCondition && !option.targetBlank) {
      let titleToSet = option.label;
      let iconToSet = option.icon;

      // Lógica para el botón "Regresar"
      if (option.label === 'Regresar' && option.redirectToLabel) {
        titleToSet = option.redirectToLabel;
      }

      dispatch(onChangePageTitle({ title: titleToSet, icon: iconToSet }));
    }
  }, [location.pathname, isActiveCondition, dispatch, option]);

  // #region LOGICA PARA BLOQUEDAR INDICADORES

  // EN CASO SEA PRODUCTION Y PARA ACEPTAR A SOLO UNOS USUARIOS
  // const user = useSelector(selectCurrentUser);
  // const isProductionEnv = window.location.href.includes('/spider/');
  // const isQaEnv = window.location.href.includes('/spider-qa/');
  // const isProductionOnly = isProductionEnv && !isQaEnv;
  // Bloqueo de Indicadores por URL
  // if (option.link === AppRoutes.indicators && isProductionOnly) {
  //   return null;
  // }
  // // Bloqueo de indicadores por Usuario
  // const allowedUsers = ['eduar.huaman', 'miguel.flower'];
  // if (option.id === 100 && !allowedUsers.includes(user?.username || '')) {
  //   return null;
  // }
  // #endregion

  const borderCondition = open && isActiveCondition;

  return (
    <Tooltip title={open ? EMPTY_TEXT : option.label} placement="right">
      <motion.div
        className="w-64"
        animate={{ width: open ? '14rem' : 'auto' }}
        transition={{ duration: !open ? 0 : 0.5 }}
        onClick={onChangeOption}
      >
        {option.hasPrevDivider && <Divider className="my-3" />}
        {option.link && (
          <NavLink
            to={option.link}
            target={option.targetBlank ? '_blank' : '_self'}
            className={`
              sidebar-item
              ${isActiveCondition ? 'active-nav' : 'default-nav'} 
              ${borderCondition ? 'border-r-4' : 'border-r-0'}
            `}
          >
            <div className="mr-2">
              <i className={`far fa-${option.icon} fa-fw`} />
            </div>
            <AnimatePresence>
              {open && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Typography.Text ellipsis>{option.label}</Typography.Text>
                </motion.div>
              )}
            </AnimatePresence>
          </NavLink>
        )}
        {option.children && (
          <div className="sidebar-item default-nav">
            <div className="mr-2">
              <i className={`far fa-${option.icon} fa-fw`} />
            </div>
            <AnimatePresence>
              {open && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Typography.Text ellipsis>{option.label}</Typography.Text>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
        {option.hasDivider && <Divider className="my-3" />}
      </motion.div>
    </Tooltip>
  );
};

export default SidebarItem;
